// Виджет условной видимости полей в формах из конструктора.
//
// На форме (<form data-widget="form,formsConditional">) ищем все .fields-row,
// у которых задан атрибут data-visibility-source — это поля, видимость
// которых зависит от значения исходного поля (data-visibility-source ⟶ key).
//
// Атрибуты на dependent-строке:
//   data-visibility-source   — машинный ключ исходного поля
//   data-visibility-operator — equals | not_equals | filled | not_filled
//   data-visibility-value    — значение для сравнения (для equals/not_equals)
//
// На исходных полях `name` совпадает с key (или name="key[]" для checkbox_group).
// При изменении любого источника пересчитываем видимость всех зависимых строк.
// Транзитивность поддерживается за счёт повторной итерации до стабилизации.

const MAX_RESOLVE_PASSES = 16;

export default function (form) {
    const dependents = Array.from(form.querySelectorAll('[data-visibility-source]'));
    if (dependents.length === 0) {
        return;
    }

    const recompute = () => {
        const visibilityById = new Map();

        for (let pass = 0; pass < MAX_RESOLVE_PASSES; pass++) {
            let changed = false;

            for (const row of dependents) {
                const id = row.dataset.fieldId || row;
                const wasVisible = visibilityById.get(id);
                const visible = resolveVisibility(form, row, dependents, visibilityById);

                if (wasVisible !== visible) {
                    visibilityById.set(id, visible);
                    changed = true;
                }
            }

            if (!changed) {
                break;
            }
        }

        for (const row of dependents) {
            const id = row.dataset.fieldId || row;
            const visible = visibilityById.get(id) ?? true;
            applyVisibility(row, visible);
        }
    };

    form.addEventListener('input', recompute);
    form.addEventListener('change', recompute);

    recompute();
}

function resolveVisibility(form, row, dependents, visibilityById) {
    const sourceKey = row.dataset.visibilitySource;
    if (!sourceKey) {
        return true;
    }

    const sourceRow = findRowByKey(form, sourceKey);
    if (sourceRow && sourceRow.dataset.visibilitySource) {
        const sourceId = sourceRow.dataset.fieldId || sourceRow;
        if (visibilityById.get(sourceId) === false) {
            return false;
        }
    }

    const operator = row.dataset.visibilityOperator || 'equals';
    const expected = row.dataset.visibilityValue ?? '';
    const value = readSourceValue(form, sourceKey);

    switch (operator) {
        case 'filled':
            return isFilled(value);
        case 'not_filled':
            return !isFilled(value);
        case 'not_equals':
            return !valueEquals(value, expected);
        case 'equals':
        default:
            return valueEquals(value, expected);
    }
}

function findRowByKey(form, key) {
    return form.querySelector(`.fields-row[data-field-key="${cssEscape(key)}"]`);
}

function readSourceValue(form, key) {
    // checkbox_group: name="key[]"
    const groupName = `${key}[]`;
    const groupInputs = form.querySelectorAll(`input[name="${cssEscape(groupName)}"]:checked`);
    if (groupInputs.length > 0) {
        return Array.from(groupInputs).map((el) => el.value);
    }

    const elements = form.querySelectorAll(
        `[name="${cssEscape(key)}"]`
    );

    if (elements.length === 0) {
        return null;
    }

    const first = elements[0];

    if (first.type === 'checkbox') {
        // одиночный checkbox
        return first.checked;
    }

    if (first.type === 'radio') {
        const checked = Array.from(elements).find((el) => el.checked);
        return checked ? checked.value : null;
    }

    if (first.tagName === 'SELECT') {
        return first.value;
    }

    return first.value;
}

function isFilled(value) {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (value === null || value === undefined) {
        return false;
    }
    return String(value).trim() !== '';
}

function valueEquals(value, expected) {
    if (Array.isArray(value)) {
        return value.some((v) => String(v) === expected);
    }
    if (typeof value === 'boolean') {
        return (value ? 'true' : 'false') === expected;
    }
    return String(value ?? '') === expected;
}

function applyVisibility(row, visible) {
    if (visible) {
        row.removeAttribute('hidden');
    } else {
        row.setAttribute('hidden', '');
        // Очищаем скрытые поля, чтобы они не попадали в сериализованный payload.
        row.querySelectorAll('input, textarea, select').forEach((el) => {
            if (el.type === 'checkbox' || el.type === 'radio') {
                el.checked = false;
            } else {
                el.value = '';
            }
        });
    }
}

function cssEscape(str) {
    // Минимальный экран для одинарных кавычек и обратных слешей —
    // в name машинных ключей это и так не должно встречаться.
    return String(str).replace(/(["\\])/g, '\\$1');
}
